import { useTranslation } from "react-i18next";
import { useTellescopeQuery } from "@tellescope/tanstack-query";
import { Select } from "@tellescope/design-system";
import { formatInitials, formatName } from "@tellescope/utils";
import { CareTeamMemberOption } from "./CareTeamMemberOption";

interface CareTeamMemberMultiSelectProps {
  careTeamUserIds: string[];
  value: string[];
  onChange: (userIds: string[]) => void;
  disabled?: boolean;
  id?: string;
}

// TODO: investigate moving to PersonSelect (@tellescope/person-select). Not moved yet because this patient-portal
//   picker is scoped to the patient's care team via getByIds rather than an org-wide search.
export const CareTeamMemberMultiSelect = ({
  careTeamUserIds,
  value,
  onChange,
  disabled,
  id,
}: CareTeamMemberMultiSelectProps) => {
  const { t } = useTranslation("messages");

  const usersQuery = useTellescopeQuery({
    model: "users",
    method: "getByIds",
    request: { ids: careTeamUserIds },
    enabled: careTeamUserIds.length > 0,
    select: (data) => [...(data?.matches ?? [])].sort((a, b) => formatName(a).localeCompare(formatName(b))),
  });

  const careTeamUsers = usersQuery.data ?? [];
  const isLoading = usersQuery.isLoading;

  const options = careTeamUsers.map((user) => ({
    user,
    label: formatName(user) || user.email || user.id,
  }));

  return (
    <Select multiple value={value} onValueChange={onChange} disabled={disabled || isLoading} maxDisplayCount={3}>
      <Select.Trigger id={id} className="w-full">
        <Select.Value placeholder={isLoading ? t("Loading care team...") : t("Select care team members")} />
      </Select.Trigger>
      <Select.Content loading={isLoading}>
        {options.map(({ user, label }) => (
          <Select.Item key={user.id} value={user.id} label={label} textValue={label}>
            <CareTeamMemberOption userId={user.id} label={label} avatar={user.avatar} initials={formatInitials(user)} />
          </Select.Item>
        ))}
      </Select.Content>
    </Select>
  );
};
